import { Component, computed, effect, inject, signal, viewChild } from '@angular/core';
import { Router } from '@angular/router';
import {
  BuildingApplicationItemDto,
  BuildingApplicationMergeDto,
  BuildingApplicationService
} from '@artemis-software/wr-api';
import { NbDialogService, NbToastrService } from '@nebular/theme';
import { firstValueFrom } from 'rxjs';
import { asyncComputed } from '@/utils/signals/asyncComputed';
import { createForm } from '@sonofabit/ng-core';
import { ConfirmDialogComponent } from '@components/dialog/confirm-dialog/confirm-dialog.component';
import { loader } from '@/utils/signals/loader';
import { routeParam } from '@/utils/signals/router';
import { PendingChanges } from '@guards/pending-changes.guard';
import {
  BuildingBaseDataFormComponent
} from '@pages/building-detail-page/building-form-page/building-base-data-form/building-base-data-form.component';
import {
  BuildingAllocationFormComponent
} from '@pages/building-detail-page/building-form-page/building-allocation-form/building-allocation-form.component';
import {
  BuildingAccessFormComponent
} from '@pages/building-detail-page/building-form-page/building-access-form/building-access-form.component';
import {
  BuildingAmenityFormComponent
} from '@pages/building-detail-page/building-form-page/building-amenity-form/building-amenity-form.component';
import { BuildingSectionEditorComponent } from '@components/building-section-editor/building-section-editor.component';

@Component({
  standalone: false,
  selector: 'wr-building-application-detail',
  templateUrl: './building-application-detail.component.html',
  styleUrl: './building-application-detail.component.scss',
})
export class BuildingApplicationDetailComponent implements PendingChanges {
  private readonly router = inject(Router);
  private readonly buildingApplicationService = inject(BuildingApplicationService);
  private readonly toastrService = inject(NbToastrService);
  private readonly dialogService = inject(NbDialogService);
  protected readonly applicationStatus = BuildingApplicationItemDto.StatusEnum;

  readonly baseDateTab = 'Grunddaten';
  readonly buildingSectionTab = 'Eingänge';

  readonly buildingApplicationId = routeParam('id', true);
  readonly loader = loader();
  readonly isEditing = signal(false);
  readonly baseDataForm = viewChild(BuildingBaseDataFormComponent);
  readonly allocationForm = viewChild(BuildingAllocationFormComponent);
  readonly accessForm = viewChild(BuildingAccessFormComponent);
  readonly amenityForm = viewChild(BuildingAmenityFormComponent);
  readonly buildingSectionEditor = viewChild(BuildingSectionEditorComponent);

  readonly buildingApplication = asyncComputed(()=>{
    const buildingApplicationId = this.buildingApplicationId();
    if (buildingApplicationId == 'new') {
      return undefined;
    }
    return this.buildingApplicationService.findById(buildingApplicationId);
  }, undefined, this.loader);

  readonly editable = computed(() => {
    const application = this.buildingApplication();
    return application?.status === this.applicationStatus.Rejected;
  });


  readonly form = computed(() => {
    const buildingApplication = this.buildingApplication();

    return createForm(({ field }) => ({
      id: field({ value: buildingApplication?.id }),
      baseData: field({ value: buildingApplication?.baseData ?? null }),
      allocationData: field({ value: buildingApplication?.allocationData ?? null }),
      accessData: field({ value: buildingApplication?.accessData ?? null }),
      amenityData: field({ value: buildingApplication?.amenityData ?? null }),
      buildingSections: field({ value: buildingApplication?.groupedBuildingSections ?? [] }),
    }));
  });

  constructor() {
    if (this.buildingApplicationId() === 'new') {
      this.isEditing.set(true);
    }

    effect(() => {
      const form = this.form();
      const editing = this.isEditing();
      const editable = this.editable();
      const isNew = this.buildingApplicationId() === 'new';

      if (editing && (isNew || editable)) {
        form.enable();
      } else {
        form.disable();
      }
    });
  }

  async submitBuildingData(): Promise<void> {
    const formGroup = this.form();
    this.markAllFormsAsTouched();

    if (formGroup.invalid) {
      this.toastrService.danger('Bitte füllen Sie alle erforderlichen Felder aus.', 'Eingabe ungültig');
      return;
    }

    const activeLoader = this.loader.add();
    try {
      activeLoader.set(true);

      const mergeDto = this.mapToApplicationMergeDto(formGroup.value);

      if (!mergeDto.buildingSectionApplications?.length) {
        this.toastrService.danger('Der Antrag muss mindestens einen Gebäudeteil enthalten.', 'Gebäudeantrag');
        return;
      }

      await firstValueFrom(this.buildingApplicationService.merge(mergeDto));

      this.isEditing.set(false);
      this.toastrService.success('Gebäude-Antrag wurde erfolgreich gespeichert', 'Erfolg');

      await this.router.navigate(['buildingApplications']);

    } catch (e) {
      console.error('Save failed:', e);
      this.toastrService.danger('Speichern fehlgeschlagen.', 'Fehler');
    } finally {
      activeLoader.stop();
    }
  }

  private markAllFormsAsTouched() {
    this.form().markAllAsTouched();
    this.baseDataForm()?.markAsTouched();
    this.allocationForm()?.markAsTouched();
    this.accessForm()?.markAsTouched();
    this.amenityForm()?.markAsTouched();
    this.buildingSectionEditor()?.markAsTouched();
  }

  private mapToApplicationMergeDto(formValue: any): BuildingApplicationMergeDto {
    return {
      id: this.buildingApplicationId() === 'new' ? undefined : this.buildingApplicationId(),
      baseData: formValue.baseData,
      accessData: formValue.accessData,
      amenityData: formValue.amenityData,
      allocationData: {
        ownerTenantId: formValue.allocationData?.ownerTenantId || formValue.allocationData?.ownerTenant?.id,
        propertyManagerId: formValue.allocationData?.propertyManager?.id,
        embeddedPropertyManager: formValue.allocationData?.embeddedPropertyManager,
        owner: formValue.allocationData?.owner,
        propertyCaretaker: formValue.allocationData?.propertyCaretaker,
        assistant: formValue.allocationData?.assistant,
      },
      buildingSectionApplications: (formValue.buildingSections ?? []).flatMap((group: any) =>
        (group.houseNumbers ?? []).map((hn: any) => ({
          id: hn.sectionId || null,
          address: {
            street: group.street,
            number: hn.number,
            zipCode: group.zipCode || group.automaticAddress?.zipCode,
            city: group.city || group.automaticAddress?.city,
            province: group.state || group.automaticAddress?.state,
            country: 'AT'
          }
        }))
      ),
    };
  }

  async reSubmitAfterRejection() {
    const application = this.buildingApplication();
    if (!application?.id) return;

    const result = await firstValueFrom(
      this.dialogService.open(ConfirmDialogComponent, {
        context: {
          header: 'Antrag erneut abgeben',
          message: 'Sind Sie sicher, dass Sie diesen Antrag erneut abgeben möchten?',
          status: 'danger'
        }
      }).onClose
    );

    if(result === true) {
      await this.submitBuildingData();
    }
  }

  toggleEdit() {
    this.isEditing.update(e=>!e);
  }

  async navigateBack(): Promise<void> {
    if (window.history.length > 1) {
      window.history.back();
    } else {
      await this.router.navigate(['buildingApplications']);
    }
  }

  hasPendingChanges(): boolean | Promise<boolean> {
    return this.isEditing();
  }

}
